import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useImpersonate } from '@/contexts/ImpersonateContext';

/**
 * Returns the current user's tenant_id from their profile.
 * Used to tag all inserts with the correct tenant for multi-tenant isolation.
 */
export function useTenantId() {
  const { user } = useAuth();

  const { data: tenantId } = useQuery({
    queryKey: ['tenant_id', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('tenant_id')
        .eq('user_id', user.id)
        .single();
      if (error) throw error;
      return (data as any)?.tenant_id as string;
    },
    enabled: !!user,
    staleTime: Infinity,
  });

  return tenantId ?? null;
}

/**
 * Returns the effective tenant_id — the impersonated tenant when impersonating,
 * otherwise the current user's own tenant.
 */
export function useEffectiveTenantId() {
  const { user, userRole } = useAuth();
  const { isImpersonating, impersonatedUserId } = useImpersonate();

  const targetUserId =
    userRole === 'super_admin' && isImpersonating && impersonatedUserId
      ? impersonatedUserId
      : user?.id;

  const { data: tenantId } = useQuery({
    queryKey: ['effective_tenant_id', targetUserId],
    queryFn: async () => {
      if (!targetUserId) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('tenant_id')
        .eq('user_id', targetUserId)
        .single();
      if (error) throw error;
      return (data as any)?.tenant_id as string;
    },
    enabled: !!targetUserId,
    staleTime: Infinity,
  });

  return tenantId ?? null;
}

/**
 * Standalone function to fetch tenant_id (for use outside hooks).
 */
export async function getTenantId(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return null;
  const { data } = await supabase
    .from('profiles')
    .select('tenant_id')
    .eq('user_id', user.id)
    .single();
  return (data as any)?.tenant_id ?? null;
}
